import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ApiIntegration.css';

const ApiIntegration = () => {
  const { t } = useTranslation('apiIntegration');
  const [activeTab, setActiveTab] = useState('connected-services');
  
  // Helper functions to translate status and use case values
  const translateServiceStatus = (status) => {
    if (!status) return '';
    const statusMap = {
      'connected': t('services.status.connected'),
      'not-setup': t('services.status.notSetup'),
      'disconnected': t('services.status.disconnected')
    };
    return statusMap[status.toLowerCase()] || status;
  };
  
  const translateUseCase = (useCase) => {
    if (!useCase) return '';
    const useCaseMap = {
      'online payments': t('services.useCases.onlinePayments'),
      'offline payments': t('services.useCases.offlinePayments'),
      'notifications': t('services.useCases.notifications'),
      'smart chatbot': t('services.useCases.smartChatbot')
    };
    return useCaseMap[useCase.toLowerCase()] || useCase;
  };
  
  const translateKeyStatus = (status) => {
    if (!status) return '';
    const statusMap = {
      'active': t('apiKeys.status.active'),
      'expires-soon': t('apiKeys.status.expiresSoon'),
      'expired': t('apiKeys.status.expired')
    };
    return statusMap[status.toLowerCase()] || status;
  };
  
  const translateWebhookStatus = (status) => {
    if (!status) return '';
    const statusMap = {
      'active': t('webhooks.status.active'),
      'failed': t('webhooks.status.failed'),
      'inactive': t('webhooks.status.inactive')
    };
    return statusMap[status.toLowerCase()] || status;
  };
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [showCreateWebhookModal, setShowCreateWebhookModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [newApiKey, setNewApiKey] = useState({
    name: '',
    role: 'Read Only',
    permissions: [],
    expiry: ''
  });
  const [newWebhook, setNewWebhook] = useState({
    event: '',
    endpoint: '',
    secret: ''
  });

  const [integrations, setIntegrations] = useState([
    {
      id: 'stripe',
      name: 'Stripe',
      status: 'connected',
      useCase: 'Online payments',
      lastSync: '2024-01-15T10:30:00Z',
      icon: '💳',
      settings: { apiKey: 'sk_test_...', webhookSecret: 'whsec_...' }
    },
    {
      id: 'baridimob',
      name: 'BaridiMob',
      status: 'not-setup',
      useCase: 'Offline payments',
      lastSync: null,
      icon: '🏦',
      settings: {}
    },
    {
      id: 'firebase',
      name: 'Firebase',
      status: 'connected',
      useCase: 'Notifications',
      lastSync: '2024-01-15T09:15:00Z',
      icon: '🔥',
      settings: { projectId: 'nativelike-app', apiKey: 'AIza...' }
    },
    {
      id: 'openai',
      name: 'OpenAI',
      status: 'connected',
      useCase: 'Smart Chatbot',
      lastSync: '2024-01-15T11:45:00Z',
      icon: '🤖',
      settings: { apiKey: 'sk-...', model: 'gpt-4' }
    }
  ]);

  const [apiKeys, setApiKeys] = useState([
    {
      id: 'mobile-app-readonly',
      name: 'MobileApp-ReadOnly',
      role: 'Read Only',
      status: 'active',
      expiry: '2025-12-01',
      permissions: ['read:users', 'read:articles'],
      ipWhitelist: ['*']
    },
    {
      id: 'school-crm-access',
      name: 'SchoolCRMAccess',
      role: 'Full Write',
      status: 'expires-soon',
      expiry: '2025-07-30',
      permissions: ['read:users', 'write:users', 'read:payments'],
      ipWhitelist: ['192.168.1.0/24']
    }
  ]);

  const [webhooks, setWebhooks] = useState([
    {
      id: 'payment-received',
      event: 'New Payment Received',
      endpoint: 'https://schoolcrm.com/payhook',
      status: 'active',
      lastSent: '2024-01-15T12:30:00Z',
      secret: 'whsec_abc123',
      retryCount: 0
    },
    {
      id: 'student-signup',
      event: 'New Student Signup',
      endpoint: 'https://myschool.dz/signupapi',
      status: 'failed',
      lastSent: '2024-01-15T12:25:00Z',
      secret: 'whsec_def456',
      retryCount: 3
    }
  ]);

  const [apiLogs, setApiLogs] = useState([
    {
      id: 1,
      time: '2024-01-15T12:30:00Z',
      endpoint: '/api/users/me',
      method: 'GET',
      user: 'MobileApp',
      status: 200,
      duration: 83,
      ip: '105.23.11.9'
    },
    {
      id: 2,
      time: '2024-01-15T12:29:00Z',
      endpoint: '/api/teachers/343',
      method: 'PUT',
      user: 'CRMWebhook',
      status: 401,
      duration: 21,
      ip: '197.99.22.1'
    }
  ]);

  const [testApi, setTestApi] = useState({
    method: 'GET',
    url: '/api/users/123/articles',
    headers: {},
    body: '',
    response: null,
    loading: false
  });

  // Define tabs - component will re-render when language changes
  const tabs = [
    { id: 'connected-services', label: t('tabs.connectedServices'), icon: 'link' },
    { id: 'api-keys', label: t('tabs.apiKeys'), icon: 'vpn_key' },
    { id: 'webhooks', label: t('tabs.webhooks'), icon: 'webhook' },
    { id: 'test-api', label: t('tabs.testApi'), icon: 'science' },
    { id: 'api-logs', label: t('tabs.apiLogs'), icon: 'analytics' }
  ];

  const handleCreateApiKey = () => {
    if (newApiKey.name && newApiKey.expiry) {
      const key = {
        id: `key-${Date.now()}`,
        name: newApiKey.name,
        role: newApiKey.role,
        status: 'active',
        expiry: newApiKey.expiry,
        permissions: newApiKey.permissions,
        ipWhitelist: ['*']
      };
      setApiKeys([...apiKeys, key]);
      setNewApiKey({ name: '', role: 'Read Only', permissions: [], expiry: '' });
      setShowCreateKeyModal(false);
    }
  };

  const handleCreateWebhook = () => {
    if (newWebhook.event && newWebhook.endpoint) {
      const webhook = {
        id: `webhook-${Date.now()}`,
        event: newWebhook.event,
        endpoint: newWebhook.endpoint,
        status: 'active',
        lastSent: null,
        secret: newWebhook.secret || `whsec_${Math.random().toString(36).substr(2, 9)}`,
        retryCount: 0
      };
      setWebhooks([...webhooks, webhook]);
      setNewWebhook({ event: '', endpoint: '', secret: '' });
      setShowCreateWebhookModal(false);
    }
  };

  const renderConnectedServices = () => (
    <div className="tab-content">
      <div className="user-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('services.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="material-icons">search</span>
        </div>
        
        <button className="add-user-btn">
          <span className="material-icons">add</span>
          {t('services.addService')}
        </button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t('services.tableHeaders.id')}</th>
              <th>{t('services.tableHeaders.service')}</th>
              <th>{t('services.tableHeaders.useCase')}</th>
              <th>{t('services.tableHeaders.status')}</th>
              <th>{t('services.tableHeaders.lastSync')}</th>
              <th>{t('services.tableHeaders.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {integrations.map((integration) => (
              <tr key={integration.id}>
                <td>{integration.id}</td>
                <td>
                  <div className="service-info">
                    <span className="service-icon">{integration.icon}</span>
                    <span className="service-name">{integration.name}</span>
                  </div>
                </td>
                <td>{translateUseCase(integration.useCase)}</td>
                <td>
                  <span className={`status-badge ${integration.status}`}>
                    {translateServiceStatus(integration.status)}
                  </span>
                </td>
                <td>
                  {integration.lastSync ? 
                    new Date(integration.lastSync).toLocaleString() : 
                    t('services.never')
                  }
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn edit" title={t('services.actions.configure')}>
                      <span className="material-icons">settings</span>
                    </button>
                    <button className="action-btn edit" title={t('services.actions.testConnection')}>
                      <span className="material-icons">wifi</span>
                    </button>
                    <button className="action-btn delete" title={t('services.actions.disconnect')}>
                      <span className="material-icons">link_off</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderApiKeys = () => (
    <div className="tab-content">
      <div className="user-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('apiKeys.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="material-icons">search</span>
        </div>
        
        <button 
          className="add-user-btn"
          onClick={() => setShowCreateKeyModal(true)}
        >
          <span className="material-icons">add</span>
          {t('apiKeys.createApiKey')}
        </button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t('apiKeys.tableHeaders.id')}</th>
              <th>{t('apiKeys.tableHeaders.keyName')}</th>
              <th>{t('apiKeys.tableHeaders.role')}</th>
              <th>{t('apiKeys.tableHeaders.status')}</th>
              <th>{t('apiKeys.tableHeaders.expires')}</th>
              <th>{t('apiKeys.tableHeaders.permissions')}</th>
              <th>{t('apiKeys.tableHeaders.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {apiKeys.map((key) => (
              <tr key={key.id}>
                <td>{key.id}</td>
                <td>{key.name}</td>
                <td>
                  <span className={`role-badge ${key.role.toLowerCase().replace(' ', '-')}`}>
                    {key.role}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${key.status}`}>
                    {translateKeyStatus(key.status)}
                  </span>
                </td>
                <td>{key.expiry}</td>
                <td>
                  <div className="permissions-list">
                    {key.permissions.map((permission, index) => (
                      <span key={index} className="permission-badge">{permission}</span>
                    ))}
                  </div>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn edit" title={t('apiKeys.actions.view')}>
                      <span className="material-icons">visibility</span>
                    </button>
                    <button className="action-btn edit" title={t('apiKeys.actions.edit')}>
                      <span className="material-icons">edit</span>
                    </button>
                    <button className="action-btn delete" title={t('apiKeys.actions.revoke')}>
                      <span className="material-icons">block</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderWebhooks = () => (
    <div className="tab-content">
      <div className="user-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('webhooks.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="material-icons">search</span>
        </div>
        
        <button 
          className="add-user-btn"
          onClick={() => setShowCreateWebhookModal(true)}
        >
          <span className="material-icons">add</span>
          {t('webhooks.createWebhook')}
        </button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t('webhooks.tableHeaders.id')}</th>
              <th>{t('webhooks.tableHeaders.event')}</th>
              <th>{t('webhooks.tableHeaders.endpoint')}</th>
              <th>{t('webhooks.tableHeaders.status')}</th>
              <th>{t('webhooks.tableHeaders.lastSent')}</th>
              <th>{t('webhooks.tableHeaders.retryCount')}</th>
              <th>{t('webhooks.tableHeaders.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {webhooks.map((webhook) => (
              <tr key={webhook.id}>
                <td>{webhook.id}</td>
                <td>{webhook.event}</td>
                <td>
                  <div className="endpoint-info">
                    <span className="endpoint-url">{webhook.endpoint}</span>
                  </div>
                </td>
                <td>
                  <span className={`status-badge ${webhook.status}`}>
                    {translateWebhookStatus(webhook.status)}
                  </span>
                </td>
                <td>
                  {webhook.lastSent ? 
                    new Date(webhook.lastSent).toLocaleString() : 
                    t('webhooks.never')
                  }
                </td>
                <td>
                  <span className={`retry-count ${webhook.retryCount > 0 ? 'warning' : ''}`}>
                    {webhook.retryCount}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn edit" title={t('webhooks.actions.test')}>
                      <span className="material-icons">play_arrow</span>
                    </button>
                    <button className="action-btn edit" title={t('webhooks.actions.edit')}>
                      <span className="material-icons">edit</span>
                    </button>
                    <button className="action-btn delete" title={t('webhooks.actions.delete')}>
                      <span className="material-icons">delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderTestApi = () => (
    <div className="tab-content">
      <h3>{t('testApi.title')}</h3>
      <p>{t('testApi.subtitle')}</p>
      
      <div className="api-test-container">
        <div className="test-controls">
          <div className="method-selector">
            <select
              value={testApi.method}
              onChange={(e) => setTestApi({...testApi, method: e.target.value})}
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>
          
          <div className="url-input">
            <input
              type="text"
              placeholder={t('testApi.endpointPlaceholder')}
              value={testApi.url}
              onChange={(e) => setTestApi({...testApi, url: e.target.value})}
            />
          </div>
          
          <button 
            className="test-btn"
            onClick={() => {
              setTestApi({...testApi, loading: true});
              // Simulate API call
              setTimeout(() => {
                setTestApi({
                  ...testApi, 
                  loading: false,
                  response: {
                    status: 200,
                    data: { message: 'Success!', timestamp: new Date().toISOString() }
                  }
                });
              }, 1000);
            }}
            disabled={testApi.loading}
          >
            {testApi.loading ? t('testApi.testing') : t('testApi.sendRequest')}
          </button>
        </div>
        
        <div className="test-panels">
          <div className="panel">
            <h4>{t('testApi.headers')}</h4>
            <textarea
              placeholder={t('testApi.headersPlaceholder')}
              value={JSON.stringify(testApi.headers, null, 2)}
              onChange={(e) => {
                try {
                  setTestApi({...testApi, headers: JSON.parse(e.target.value)});
                } catch (error) {
                  // Handle invalid JSON
                }
              }}
            />
          </div>
          
          <div className="panel">
            <h4>{t('testApi.requestBody')}</h4>
            <textarea
              placeholder={t('testApi.requestBodyPlaceholder')}
              value={testApi.body}
              onChange={(e) => setTestApi({...testApi, body: e.target.value})}
            />
          </div>
        </div>
        
        {testApi.response && (
          <div className="response-panel">
            <h4>{t('testApi.response')}</h4>
            <div className={`response-status ${testApi.response.status >= 200 && testApi.response.status < 300 ? 'success' : 'error'}`}>
              {t('testApi.status')}: {testApi.response.status}
            </div>
            <pre className="response-body">
              {JSON.stringify(testApi.response.data, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );

  const renderApiLogs = () => (
    <div className="tab-content">
      <div className="user-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('apiLogs.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="material-icons">search</span>
        </div>
        
        <div className="log-controls">
          <button className="add-user-btn">
            <span className="material-icons">download</span>
            {t('apiLogs.exportLogs')}
          </button>
          <button className="add-user-btn">
            <span className="material-icons">refresh</span>
            {t('apiLogs.refresh')}
          </button>
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t('apiLogs.tableHeaders.id')}</th>
              <th>{t('apiLogs.tableHeaders.time')}</th>
              <th>{t('apiLogs.tableHeaders.endpoint')}</th>
              <th>{t('apiLogs.tableHeaders.method')}</th>
              <th>{t('apiLogs.tableHeaders.user')}</th>
              <th>{t('apiLogs.tableHeaders.status')}</th>
              <th>{t('apiLogs.tableHeaders.duration')}</th>
              <th>{t('apiLogs.tableHeaders.ipAddress')}</th>
            </tr>
          </thead>
          <tbody>
            {apiLogs.map((log) => (
              <tr key={log.id}>
                <td>{log.id}</td>
                <td>{new Date(log.time).toLocaleString()}</td>
                <td>{log.endpoint}</td>
                <td>
                  <span className={`method-badge ${log.method.toLowerCase()}`}>
                    {log.method}
                  </span>
                </td>
                <td>{log.user}</td>
                <td>
                  <span className={`status-badge ${log.status >= 200 && log.status < 300 ? 'success' : 'error'}`}>
                    {log.status}
                  </span>
                </td>
                <td>{log.duration}ms</td>
                <td>{log.ip}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'connected-services':
        return renderConnectedServices();
      case 'api-keys':
        return renderApiKeys();
      case 'webhooks':
        return renderWebhooks();
      case 'test-api':
        return renderTestApi();
      case 'api-logs':
        return renderApiLogs();
      default:
        return renderConnectedServices();
    }
  };

  return (
    <div id="api-integration">
      <div className="section">
        <div className="user-management-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="material-icons">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
        
        {renderTabContent()}
      </div>

      {/* API Key Creation Modal */}
      {showCreateKeyModal && (
        <div className="user-form-overlay" onClick={() => setShowCreateKeyModal(false)}>
          <div className="user-form" onClick={(e) => e.stopPropagation()}>
            <h3>{t('apiKeys.modals.createNewApiKey')}</h3>
            <input
              type="text"
              placeholder={t('apiKeys.modals.keyName')}
              value={newApiKey.name}
              onChange={(e) => setNewApiKey({...newApiKey, name: e.target.value})}
            />
            <select
              value={newApiKey.role}
              onChange={(e) => setNewApiKey({...newApiKey, role: e.target.value})}
            >
              <option value="Read Only">{t('apiKeys.modals.roles.readOnly')}</option>
              <option value="Full Write">{t('apiKeys.modals.roles.fullWrite')}</option>
              <option value="Admin">{t('apiKeys.modals.roles.admin')}</option>
            </select>
            <input
              type="date"
              placeholder={t('apiKeys.modals.expiryDate')}
              value={newApiKey.expiry}
              onChange={(e) => setNewApiKey({...newApiKey, expiry: e.target.value})}
            />
            <div className="form-buttons">
              <button onClick={() => setShowCreateKeyModal(false)}>{t('apiKeys.modals.cancel')}</button>
              <button onClick={handleCreateApiKey}>{t('apiKeys.modals.createKey')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Webhook Creation Modal */}
      {showCreateWebhookModal && (
        <div className="user-form-overlay" onClick={() => setShowCreateWebhookModal(false)}>
          <div className="user-form" onClick={(e) => e.stopPropagation()}>
            <h3>{t('webhooks.modals.createNewWebhook')}</h3>
            <input
              type="text"
              placeholder={t('webhooks.modals.eventName')}
              value={newWebhook.event}
              onChange={(e) => setNewWebhook({...newWebhook, event: e.target.value})}
            />
            <input
              type="url"
              placeholder={t('webhooks.modals.endpointUrl')}
              value={newWebhook.endpoint}
              onChange={(e) => setNewWebhook({...newWebhook, endpoint: e.target.value})}
            />
            <input
              type="text"
              placeholder={t('webhooks.modals.secret')}
              value={newWebhook.secret}
              onChange={(e) => setNewWebhook({...newWebhook, secret: e.target.value})}
            />
            <div className="form-buttons">
              <button onClick={() => setShowCreateWebhookModal(false)}>{t('webhooks.modals.cancel')}</button>
              <button onClick={handleCreateWebhook}>{t('webhooks.modals.createWebhook')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiIntegration; 